import { computed, ref, watch, nextTick, onUnmounted, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useUiStore } from '@Stores';

export function useMobileDatePicker(calendarRef: Ref<any>, inputId?: string) {
  const { appWidth } = storeToRefs(useUiStore());

  const isInputClick = ref(false);

  const isMobile = computed(() => appWidth.value <= 768);

  // Track cleanup
  const cleanupFns: (() => void)[] = [];
  let isSetup = false;

  const calendarPassthroughProps = computed(() => ({
    pcinputtext: {
      root: {
        inputmode: 'text',
        onclick: isMobile.value ? handleInputClick : undefined,
      },
    },
    trigger: isMobile.value ? { root: { class: 'hidden' } } : {},
  }));

  const stopWatch = watch(
    () => calendarRef.value?.overlayVisible,
    (isVisible) => handleOverlayVisibilityChange(isVisible)
  );
  cleanupFns.push(stopWatch);

  function handleInputClick(e: Event) {
    e.stopPropagation();

    if (calendarRef.value) calendarRef.value.overlayVisible = false;

    setTimeout(() => triggerKeyboard(), 0);
  }

  function handleOverlayVisibilityChange(isVisible: boolean) {
    if (!isMobile.value || !isVisible || !isInputClick.value) return;

    if (calendarRef.value) calendarRef.value.overlayVisible = false;

    isInputClick.value = false;
  }

  function triggerKeyboard() {
    const input = findInput();

    if (!input) return;

    if (document.activeElement !== input && document.activeElement !== null) return;

    input.removeAttribute('readonly');
    input.removeAttribute('disabled');
    input.setAttribute('inputmode', 'text');

    if (document.activeElement !== input) input.focus();
  }

  function findInput(): HTMLInputElement | null {
    if (inputId) return document.getElementById(inputId) as HTMLInputElement;

    const calendarRoot = calendarRef.value?.$el?.closest('.p-calendar') || calendarRef.value?.$el;

    return calendarRoot?.querySelector('input') as HTMLInputElement;
  }

  function findCalendarRoot(input: HTMLInputElement): Element | null {
    if (inputId) return input.closest('.p-calendar');

    return calendarRef.value?.$el?.closest('.p-calendar') || calendarRef.value?.$el;
  }

  function setupMobileInputClickHandler() {
    if (!isMobile.value || isSetup) return;

    function setup() {
      const input = findInput();
      if (!input) return;

      const calendarRoot = findCalendarRoot(input);
      if (!calendarRoot) return;

      const inputWrapper = input.closest('.p-inputtext');
      isSetup = true;

      function handleClick(e: Event) {
        const target = e.target as HTMLElement;
        const icon = target.closest('.p-datepicker-trigger');
        const calendarButton = target.closest('button[class*="af-button"]');

        if (calendarButton || icon) return;

        if (target === input || (inputWrapper && target === inputWrapper)) {
          isInputClick.value = true;

          e.stopImmediatePropagation();
          e.stopPropagation();

          if (calendarRef.value) calendarRef.value.overlayVisible = false;

          setTimeout(() => {
            if (document.activeElement === input || e.target === input) triggerKeyboard();
          }, 0);
        }
      }

      // Add listeners and track for cleanup
      function addListener(el: Element | null, event: string) {
        if (!el) return;

        el.addEventListener(event, handleClick, true);

        cleanupFns.push(() => el.removeEventListener(event, handleClick, true));
      }

      addListener(calendarRoot, 'click');
      addListener(calendarRoot, 'mousedown');
      addListener(inputWrapper, 'click');
      addListener(inputWrapper, 'mousedown');
      addListener(input, 'click');
      addListener(input, 'mousedown');
    }

    if (inputId) return setup();

    nextTick(() => setup());
  }

  function openCalendarOverlay() {
    if (!calendarRef.value) return;

    isInputClick.value = false;

    if (calendarRef.value.show) return calendarRef.value.show();

    calendarRef.value.overlayVisible = true;
  }

  function cleanup() {
    cleanupFns.forEach((fn) => fn());
    cleanupFns.length = 0;
    isSetup = false;
  }

  // Auto-cleanup on unmount if used in setup context
  onUnmounted(cleanup);

  return {
    isMobile,
    isInputClick,
    handleInputClick,
    handleOverlayVisibilityChange,
    setupMobileInputClickHandler,
    triggerKeyboard,
    openCalendarOverlay,
    calendarPassthroughProps,
    cleanup, // Expose for manual cleanup if needed
  };
}
